import { useState } from 'react';
import { Heart, Repeat, ArrowRight, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { postService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { UserAvatar } from '@/components/ui/Avatar';
import QuotedContent from '@/components/feed/QuotedContent';
import { useLongPress } from '@/hooks/useLongPress';
import { formatRelativeDate, cn } from '@/lib/utils';
import { ROUTES } from '@/constants';

interface ReplyCardProps {
  reply: any;
  depth: number;
  childrenMap: Map<string, any[]>;
  replyContent: string;
  setReplyContent: (value: string) => void;
  replyingToId: string | null;
  onStartReply: (replyId: string) => void;
  onSubmitReply: (e: React.FormEvent, parentId: string) => void;
  onQuote: (reply: any) => void;
  onDelete: (replyId: string) => void;
}

export default function ReplyCard({
  reply,
  depth,
  childrenMap,
  replyContent,
  setReplyContent,
  replyingToId,
  onStartReply,
  onSubmitReply,
  onQuote,
  onDelete,
}: ReplyCardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isLiked, setIsLiked] = useState<boolean>(
    () => !!reply.likes?.some((l: any) => l.user_id === user?.id),
  );
  const [likeCount, setLikeCount] = useState<number>(reply.likes?.length || 0);
  const [isReposted, setIsReposted] = useState<boolean>(
    () => !!reply.reposts?.some((r: any) => r.user_id === user?.id),
  );
  const [repostCount, setRepostCount] = useState<number>(reply.reposts?.length || 0);

  const handleLike = async () => {
    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }
    const prevLiked = isLiked;
    const prevCount = likeCount;
    setIsLiked(!prevLiked);
    setLikeCount((c) => (prevLiked ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevLiked
        ? await postService.unlikePost(reply.id)
        : await postService.likePost(reply.id);
      if (error) throw error;
    } catch (err: any) {
      setIsLiked(prevLiked);
      setLikeCount(prevCount);
      toast('Failed', err?.message || 'Could not update like.', 'destructive');
    }
  };

  const handleRepost = async () => {
    if (!user) {
      navigate(ROUTES.LOGIN);
      return;
    }
    const prevReposted = isReposted;
    const prevCount = repostCount;
    setIsReposted(!prevReposted);
    setRepostCount((c) => (prevReposted ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevReposted
        ? await postService.unrepostPost(reply.id)
        : await postService.repostPost(reply.id);
      if (error) throw error;
    } catch (err: any) {
      setIsReposted(prevReposted);
      setRepostCount(prevCount);
      toast('Failed', err?.message || 'Could not update repost.', 'destructive');
    }
  };

  const {
    pressing: longPressing,
    start: startLongPress,
    end: endLongPress,
    cancel: cancelLongPress,
    move: handleTouchMove,
  } = useLongPress({
    onLongPress: () => onQuote(reply),
    onShortPress: () => void handleRepost(),
  });

  const profileHref = ROUTES.PROFILE.replace(':username', reply.profiles?.username || 'me');
  const children = childrenMap.get(reply.id) || [];
  const isOwn = !!user && reply.user_id === user.id;

  return (
    <div
      className={cn(
        'border-b border-neutral-200 dark:border-neutral-800',
        depth === 0 ? 'p-3' : 'py-3 pl-2',
      )}
    >
      <div className="flex gap-3">
        <Link to={profileHref} className="shrink-0">
          <UserAvatar src={reply.profiles?.avatar_url} size={depth === 0 ? 'md' : 'sm'} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Link to={profileHref} className="max-w-full truncate font-semibold hover:underline">
              {reply.profiles?.display_name || reply.profiles?.username}
            </Link>
            <span className="max-w-full truncate text-sm text-neutral-500 dark:text-neutral-400">
              @{reply.profiles?.username}
            </span>
            <span className="text-xs text-neutral-400 dark:text-neutral-500">
              · {formatRelativeDate(reply.created_at)}
            </span>
            {isOwn && <span className="text-xs text-neutral-400 dark:text-neutral-500">· You</span>}
          </div>

          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            {reply.content}
          </div>

          {reply.quoted_post && <QuotedContent post={reply.quoted_post} />}

          {reply.post_media && reply.post_media.length > 0 && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              {reply.post_media.map((m: any, i: number) => (
                <img
                  key={i}
                  src={m.url}
                  alt="Reply media"
                  loading="lazy"
                  className="aspect-square w-full rounded-xl object-cover"
                />
              ))}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1 text-sm sm:gap-2">
            <button
              onClick={handleLike}
              className={cn(
                'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                isLiked ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-600 dark:text-neutral-300',
              )}
              aria-label={isLiked ? 'Unlike reply' : 'Like reply'}
            >
              <Heart size={16} className={cn(isLiked && 'fill-current')} />
              <span>{likeCount}</span>
            </button>

            <button
              onClick={() => onStartReply(reply.id)}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              aria-label="Reply"
            >
              <ArrowRight size={16} />
              <span>Reply</span>
            </button>

            <button
              type="button"
              onMouseDown={startLongPress}
              onMouseUp={endLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={startLongPress}
              onTouchEnd={endLongPress}
              onTouchCancel={cancelLongPress}
              onTouchMove={handleTouchMove}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void handleRepost();
                }
              }}
              className={cn(
                'flex select-none touch-manipulation items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
                longPressing
                  ? 'animate-shake-scale text-emerald-600 dark:text-emerald-400'
                  : isReposted
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-neutral-600 dark:text-neutral-300',
              )}
              aria-label={isReposted ? 'Undo repost (hold to quote)' : 'Repost (hold to quote)'}
              title="Tap to repost, hold to quote"
            >
              <Repeat size={16} />
              <span>{repostCount}</span>
            </button>

            {isOwn && (
              <button
                onClick={() => onDelete(reply.id)}
                className="ml-auto flex items-center rounded-lg px-2 py-1.5 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                aria-label="Delete reply"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {replyingToId === reply.id && (
        <div className="mt-3">
          <form onSubmit={(e) => onSubmitReply(e, reply.id)} className="flex items-center gap-3">
            <UserAvatar size="sm" className="shrink-0" />
            <input
              type="text"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={`Reply to @${reply.profiles?.username || 'user'}...`}
              className="min-w-0 flex-1 bg-transparent text-sm focus:ring-0 placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
            />
            <button
              type="submit"
              disabled={!replyContent.trim()}
              className="shrink-0 text-sm font-medium text-brand-600 dark:text-brand-400 disabled:opacity-50"
            >
              Reply
            </button>
          </form>
        </div>
      )}

      {children.length > 0 && (
        <div className="mt-2 ml-8 border-l-2 border-neutral-100 pl-2 dark:border-neutral-800">
          {children.map((child: any) => (
            <ReplyCard
              key={child.id}
              reply={child}
              depth={depth + 1}
              childrenMap={childrenMap}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              replyingToId={replyingToId}
              onStartReply={onStartReply}
              onSubmitReply={onSubmitReply}
              onQuote={onQuote}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
